import React, { useState } from 'react';
import { SHOP_TABS, SHOP_ITEMS, IMAGES } from '../mock/data';
import { useDemo } from '../context/DemoContext';
import { Reveal, Eyebrow } from '../components/xc/Bits';
import { InvestorNote } from '../components/xc/InvestorNote';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import { Coins, Check, Lock, Info, ShoppingBag } from 'lucide-react';

const rarityColor = { Legendary: '#E0B85A', Epic: '#B026FF', Rare: '#FF204E', 'Mission Unlock': '#2FBF71' };

export default function Shop() {
  const [tab, setTab] = useState('Clothing');
  const { loyaltyCoins, ownedItems, spendCoins, ownItem } = useDemo();
  const { toast } = useToast();

  const buy = (item) => {
    if (ownedItems.includes(item.id)) return;
    if (item.price === 0) { toast({ title: 'Mission Unlock', description: `${item.name} is earned by completing The Loyalty Job.` }); return; }
    if (loyaltyCoins < item.price) { toast({ title: 'Not enough Loyalty Coins', description: `You need ${(item.price - loyaltyCoins).toLocaleString()} more LC.` }); return; }
    if (spendCoins(item.price)) { ownItem(item.id); toast({ title: 'Acquired', description: `${item.name} added to your collection.` }); }
  };

  const items = SHOP_ITEMS.filter((i) => i.tab === tab);

  return (
    <div className="bg-[#070609] min-h-screen">
      <Toaster />
      <section className="relative overflow-hidden border-b border-white/5">
        <img src={IMAGES.lounge} alt="" className="absolute inset-0 h-full w-full object-cover opacity-40" />
        <div className="absolute inset-0 city-overlay" />
        <div className="relative max-w-[1440px] mx-auto px-4 lg:px-8 py-14 flex items-end justify-between flex-wrap gap-4">
          <div>
            <Eyebrow color="#E0B85A">Loyalty Store</Eyebrow>
            <h1 className="font-display font-700 text-3xl sm:text-4xl md:text-5xl text-white tracking-tight">THE UNDERGROUND <span className="neon-gold">MARKET</span></h1>
            <p className="mt-3 text-white/60 max-w-xl">Spend Loyalty Coins on cinematic assets. Not a cash store — LC is internal Xincity progression currency.</p>
            <div className="mt-3"><InvestorNote id="shop" title="Why LC can't be bought">
              Loyalty Coins are earned only through play and missions, never purchased. This keeps the economy a pure retention sink — no pay-to-win optics, no secondary-market risk, and a clean line between entertainment spend and gambling spend for regulators.
            </InvestorNote></div>
          </div>
          <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-[#E0B85A]/30 bg-[#E0B85A]/8">
            <Coins className="h-5 w-5 text-[#E0B85A]" /><span className="font-display font-700 text-[#E0B85A]">{loyaltyCoins.toLocaleString()} LC</span>
          </div>
        </div>
      </section>

      <section className="max-w-[1440px] mx-auto px-4 lg:px-8 py-8">
        <div className="flex gap-2 mb-6 border-b border-white/8 overflow-x-auto no-scrollbar">
          {SHOP_TABS.map((t) => (
            <button key={t} onClick={() => setTab(t)} className={`px-4 py-3 text-sm font-500 relative whitespace-nowrap transition-colors ${tab === t ? 'text-white' : 'text-white/45 hover:text-white/75'}`}>{t}{tab === t && <span className="absolute bottom-0 left-0 right-0 h-0.5" style={{ background: 'linear-gradient(90deg,#E0B85A,#B026FF)' }} />}</button>
          ))}
        </div>

        {items.length === 0 ? (
          <div className="text-center py-20 text-white/40"><ShoppingBag className="h-8 w-8 mx-auto mb-3 opacity-50" />More items arriving in a future season.</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {items.map((item, i) => {
              const owned = ownedItems.includes(item.id);
              const mission = item.price === 0;
              return (
                <Reveal key={item.id} delay={i * 60}>
                  <div className="xc-card rounded-2xl overflow-hidden group">
                    <div className="relative h-52">
                      <img src={item.img} alt={item.name} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0D0B10] via-transparent to-transparent" />
                      <span className="absolute top-3 left-3 text-[10px] font-600 tracking-[0.12em] uppercase px-2.5 py-1 rounded-full" style={{ color: rarityColor[item.rarity], border: `1px solid ${rarityColor[item.rarity]}55`, background: `${rarityColor[item.rarity]}18` }}>{item.rarity}</span>
                      {owned && <span className="absolute top-3 right-3 text-[10px] font-600 px-2.5 py-1 rounded-full bg-[#2FBF71]/20 border border-[#2FBF71]/50 text-[#2FBF71] flex items-center gap-1"><Check className="h-3 w-3" /> OWNED</span>}
                    </div>
                    <div className="p-5">
                      <div className="font-display font-700 text-white text-lg">{item.name}</div>
                      <p className="text-sm text-white/50 mt-1 leading-relaxed min-h-[40px]">{item.desc}</p>
                      <div className="mt-4 flex items-center justify-between">
                        <div className="flex items-center gap-1.5">{mission ? <span className="text-sm text-[#2FBF71] font-500 flex items-center gap-1"><Lock className="h-4 w-4" /> Mission Unlock</span> : <><Coins className="h-4 w-4 text-[#E0B85A]" /><span className="font-display font-700 text-[#E0B85A]">{item.price.toLocaleString()} LC</span></>}</div>
                        <button onClick={() => buy(item)} disabled={owned}
                          className={`rounded-lg px-4 py-2 text-sm font-600 ${owned ? 'btn-ghost opacity-60 cursor-default' : mission ? 'btn-ghost' : 'btn-gold'}`}>
                          {owned ? 'Owned' : mission ? 'Details' : 'Acquire'}
                        </button>
                      </div>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        )}

        <div className="mt-10 rounded-2xl p-6 xc-glass border border-white/8">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-[#B026FF] flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-600 text-white mb-1">About Loyalty Coins</div>
              <p className="text-sm text-white/55 leading-relaxed">Loyalty Coins are fictional and used only for Xincity progression and virtual items. They are non-withdrawable, non-transferable, not cryptocurrency, not cash and not player-to-player money. They cannot be traded externally.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
